/**
 * @file MilkPowderContractPriceModal.js
 * @version 2.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-08
 * @modified 2026-10-08
 * @description Add / edit milk powder contract SALE prices (per bag) for a
 *              customer + product. Each price runs From one day To another
 *              (2.0.0, Cam 2026-10-08 — it was a month). Adding: one or more
 *              rows in a single pass — "add another price" starts the next row
 *              the day after the last row's To — each saved as its own price;
 *              a row with exactly an existing price's dates replaces it, and
 *              one overlapping any other price is refused (here first, then by
 *              the server, utils/milkPowderDates). Editing (`price`, or Edit
 *              from view mode on the latest price) changes that one price's
 *              dates, sale price and notes through PUT. Shows a live margin
 *              hint per row against the product's current weekly cost, plus
 *              the latest price on record above the rows. A new price opens on
 *              this month (or the stretch after the latest price when this
 *              month already has one) with the cursor in the sale price.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import Input from './Input';
import DateInput from './DateInput';
import SearchableDropdown from './SearchableDropdown';
import ApplyScopeControl from './ApplyScopeControl';
import { formatCurrency } from '../utils/currencyFormatter';
import { addressLine1 } from '../utils/formatAddress';
import { useSlideOutButton } from './SlideOutButton';
import ItemAccountsPanel from './ItemAccountsPanel';
import {
  toDay, monthEndOf, rangeOf, rangesOverlap, rangeProblem, findOverlap, overlapMessage, formatRange, defaultRangeFor
} from '../utils/milkPowderDates';

// The day after a 'YYYY-MM-DD' day.
const dayAfter = (day) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

// `price` — one recorded price to edit (from the history window). Without it
// the window adds prices; view mode shows the latest and its Edit edits that.
function MilkPowderContractPriceModal({ pair, price = null, onClose, onSave, mode: initialMode = 'edit' }) {
  // Local mode so the view-mode Edit button can flip view → edit in place.
  const [mode, setMode] = useState(initialMode);
  const isView = mode === 'view';
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [existing, setExisting] = useState([]); // existing prices for the chosen customer+product
  // The date auto-fill must wait for the history to arrive, otherwise it would
  // decide against an empty list and never correct itself.
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Pricing-group scope (parent + pricing-linked children). Prefilled from the pair when editing.
  const [pricingGroup, setPricingGroup] = useState([]);
  const [applyScope, setApplyScope] = useState(
    ['parent', 'group', 'specific'].includes(pair?.apply_scope) ? pair.apply_scope : 'parent'
  );
  const [scopeAccountIds, setScopeAccountIds] = useState(
    Array.isArray(pair?.scope_accounts) ? pair.scope_accounts.map(a => a.id) : []
  );

  useSlideOutButton([loading, existing.length, pricingGroup.length, applyScope, mode, historyLoaded]);

  const today = toDay(new Date());
  const [form, setForm] = useState({
    account_id: pair?.account_id ? String(pair.account_id) : '',
    inventory_item_id: pair?.inventory_item_id ? String(pair.inventory_item_id) : '',
    notes: price?.notes || ''
  });

  // One row per price being entered. The "add" button on the last row appends
  // a row starting the day after the last row's To, so a run of prices can be
  // entered in one go, each saved as its own price. Keys are synthetic (dates
  // can be re-picked, so they can't identify a row while it is edited). `id`
  // is set on the one row of an edit; `toTouched` stops a From change from
  // moving a To the user chose.
  const nextRowKey = useRef(0);
  const makeRow = (valid_from, valid_to, sale_price = '', id = null) => ({
    key: nextRowKey.current++, id, valid_from, valid_to, sale_price, toTouched: !!id
  });
  const [rows, setRows] = useState(() => {
    if (price) {
      const r = rangeOf(price);
      return [makeRow(r?.from || '', r?.to || '', price.sale_price != null ? String(parseFloat(price.sale_price)) : '', price.id)];
    }
    const r = defaultRangeFor([], today);
    return [makeRow(r.from, r.to)];
  });
  const editingId = rows.length === 1 ? rows[0].id : null;
  // Set to the key of a newly-added row so its sale price gets focus.
  const [focusRowKey, setFocusRowKey] = useState(null);

  const locked = !!pair; // customer + product fixed when opened from a row

  const setRow = (key, field, value) => {
    setRows(prev => prev.map((r) => {
      if (r.key !== key) return r;
      if (field === 'valid_from') {
        // To follows From to the end of its month until it is picked by hand,
        // and is never left before From.
        const keepTo = r.toTouched && r.valid_to && value && r.valid_to >= value;
        return { ...r, valid_from: value, valid_to: keepTo ? r.valid_to : (monthEndOf(value) || r.valid_to) };
      }
      if (field === 'valid_to') return { ...r, valid_to: value, toTouched: true };
      return { ...r, [field]: value };
    }));
    setError('');
  };

  // Append a row starting the day after the last row's To, running to the end
  // of that day's month.
  const addRow = () => {
    const last = rows[rows.length - 1];
    const from = toDay(last.valid_to) ? dayAfter(toDay(last.valid_to)) : today;
    const row = makeRow(from, monthEndOf(from));
    setRows(prev => [...prev, row]);
    setFocusRowKey(row.key);
  };

  // Focus (and select) the new row's sale price once it has rendered.
  useEffect(() => {
    if (focusRowKey == null) return;
    const el = document.getElementById(`mp-sale-${focusRowKey}`);
    if (el) { el.focus(); el.select?.(); }
    setFocusRowKey(null);
  }, [focusRowKey]);

  useEffect(() => {
    document.body.classList.add('modal-open');
    const cw = document.querySelector('.content-wrapper');
    if (cw) cw.classList.add('modal-open');
    loadProducts();
    if (!locked) loadCustomers();
    return () => {
      document.body.classList.remove('modal-open');
      const c = document.querySelector('.content-wrapper');
      if (c) c.classList.remove('modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a customer + product is chosen, load any existing price history so we
  // can tell the user the entry is added to that record (not duplicated).
  useEffect(() => {
    if (!form.account_id || !form.inventory_item_id) { setExisting([]); setHistoryLoaded(false); return; }
    let cancelled = false;
    setHistoryLoaded(false);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/pricing/milk-powder/contract-prices/${form.account_id}/${form.inventory_item_id}/history`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled) { setExisting(Array.isArray(data) ? data : []); setHistoryLoaded(true); }
      } catch (e) { if (!cancelled) { setExisting([]); setHistoryLoaded(true); } }
    })();
    return () => { cancelled = true; };
  }, [form.account_id, form.inventory_item_id]);

  // Pre-pick the dates of a new price: this month, or the stretch after the
  // latest price when this month already has one (defaultRangeFor). Only ever
  // touches a single untouched new row, so it can't clobber anything typed or
  // picked; keyed by customer+product so changing either re-decides. In view
  // mode the row shows the latest price instead, and Edit edits that price.
  const autoFilledForRef = useRef(null);
  useEffect(() => {
    if (!historyLoaded || price) return;
    const key = `${form.account_id}:${form.inventory_item_id}:${isView ? 'view' : 'new'}`;
    if (autoFilledForRef.current === key) return;
    if (isView) {
      autoFilledForRef.current = key;
      const last = existing.length ? existing[existing.length - 1] : null;
      const r = last ? rangeOf(last) : null;
      if (last && r) {
        setRows([makeRow(r.from, r.to, last.sale_price != null ? String(parseFloat(last.sale_price)) : '', last.id)]);
        setForm(prev => ({ ...prev, notes: last.notes || '' }));
      }
      return;
    }
    if (rows.length !== 1 || rows[0].id != null || rows[0].sale_price !== '' || rows[0].toTouched) return;
    autoFilledForRef.current = key;
    const target = defaultRangeFor(existing, today);
    // Update in place (same row key) so the focused sale price input isn't remounted.
    setRows(prev => (prev.length === 1
      ? [{ ...prev[0], valid_from: target.from, valid_to: target.to }]
      : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyLoaded, existing, form.account_id, form.inventory_item_id, isView]);

  // An edit opens with the customer + product already fixed, so put the cursor
  // straight in the sale price. (Add mode opens the customer picker instead.)
  useEffect(() => {
    if (!locked || isView) return;
    setFocusRowKey(rows[0]?.key ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the selected customer's pricing group so the "Applies to" control can offer
  // Whole group / Parent only / Specific. Empty (control hidden) when not in a group.
  useEffect(() => {
    if (!form.account_id) { setPricingGroup([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/contacts/pricing-group/${form.account_id}`, { credentials: 'include' });
        if (!res.ok) { if (!cancelled) setPricingGroup([]); return; }
        const data = await res.json();
        if (cancelled) return;
        const members = data.members || [];
        setPricingGroup(members);
        if (members.length <= 1) { setApplyScope('parent'); setScopeAccountIds([]); }
      } catch (e) { if (!cancelled) setPricingGroup([]); }
    })();
    return () => { cancelled = true; };
  }, [form.account_id]);

  const loadCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/contacts/search?limit=10000&contactStatus=all&accountType=CUSTOMER`, { credentials: 'include' });
      const data = await res.json();
      setCustomers(data.contacts || []);
    } catch (e) { console.error('Error loading customers:', e); }
  };
  const loadProducts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/milk-powder/cost-prices`, { credentials: 'include' });
      const data = await res.json();
      setProducts(data.data || []);
    } catch (e) { console.error('Error loading products:', e); }
  };

  const handleChange = (f, v) => { setForm(prev => ({ ...prev, [f]: v })); setError(''); };
  const handleClose = () => { setClosing(true); setTimeout(() => onClose(), 200); };

  // Most recent sale price on record for this customer + product (history comes
  // back oldest-first), shown above the entry rows purely for reference.
  const latest = existing.length ? existing[existing.length - 1] : null;
  const latestSale = latest != null ? parseFloat(latest.sale_price) : null;
  const latestCost = latest?.cost_per_bag != null ? parseFloat(latest.cost_per_bag) : null;
  const latestMargin = (latestSale != null && latestCost != null && !isNaN(latestSale) && !isNaN(latestCost))
    ? latestSale - latestCost : null;

  // Live margin hint vs the product's current weekly cost (per bag).
  const selProduct = products.find(p => String(p.inventory_item_id) === String(form.inventory_item_id));
  const costPerBag = (selProduct && selProduct.current_cost != null && selProduct.bags_per_tonne)
    ? parseFloat(selProduct.current_cost) / parseFloat(selProduct.bags_per_tonne) : null;
  const marginFor = (row) => (costPerBag != null && row.sale_price !== '' && !isNaN(parseFloat(row.sale_price)))
    ? parseFloat(row.sale_price) - costPerBag : null;

  // Which existing price a row would replace (exactly its dates), and which it
  // would clash with — the server's rule (utils/milkPowderDates), checked here
  // first so the person sees it before anything is sent.
  const replacedBy = (row) => {
    if (row.id != null) return null;
    const from = toDay(row.valid_from);
    const to = toDay(row.valid_to);
    return existing.find((r) => { const rr = rangeOf(r); return rr && rr.from === from && rr.to === to; }) || null;
  };
  const rowProblem = (row, all) => {
    const problem = rangeProblem(row.valid_from, row.valid_to);
    if (problem) return problem;
    const range = { from: toDay(row.valid_from), to: toDay(row.valid_to) };
    const replaced = replacedBy(row);
    const clash = findOverlap(range, existing, { excludeId: row.id ?? replaced?.id ?? null });
    if (clash) return overlapMessage(clash);
    const sibling = all.find(o => o.key !== row.key && rangesOverlap(range, { from: toDay(o.valid_from), to: toDay(o.valid_to) }));
    if (sibling) return `Two of the prices being entered overlap (${formatRange(range)} and ${formatRange({ from: toDay(sibling.valid_from), to: toDay(sibling.valid_to) })}) — each day can have only one price.`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.account_id) { setError('Please select a customer'); return; }
    if (!form.inventory_item_id) { setError('Please select a product'); return; }
    if (rows.some(r => r.sale_price === '' || isNaN(parseFloat(r.sale_price)))) {
      setError(rows.length > 1 ? 'Please enter a valid sale price for every price' : 'Please enter a valid sale price');
      return;
    }
    for (const row of rows) {
      const problem = rowProblem(row, rows);
      if (problem) { setError(rows.length > 1 ? `${problem} (${formatRange({ from: toDay(row.valid_from) || row.valid_from, to: toDay(row.valid_to) || row.valid_to })})` : problem); return; }
    }
    setLoading(true);
    try {
      // One request per price. Sequential so a failure reports the price it
      // hit rather than leaving an unclear partial result.
      for (const row of rows) {
        const editing = row.id != null;
        const res = await fetch(`${API_URL}/api/pricing/milk-powder/contract-prices${editing ? `/${row.id}` : ''}`, {
          method: editing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            account_id: parseInt(form.account_id),
            inventory_item_id: parseInt(form.inventory_item_id),
            valid_from: row.valid_from,
            valid_to: row.valid_to,
            sale_price: parseFloat(row.sale_price),
            notes: form.notes || null,
            apply_scope: applyScope,
            specific_account_ids: applyScope === 'specific' ? scopeAccountIds : null
          })
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          const label = formatRange({ from: row.valid_from, to: row.valid_to });
          throw new Error(rows.length > 1 ? `${d.error || 'Failed to save price'} (${label})` : (d.error || 'Failed to save price'));
        }
      }
      onSave();
    } catch (err) {
      console.error('Error saving contract price:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // "Applies to" control — shown only when the customer is in a pricing group.
  const renderApplyScope = () => {
    if (pricingGroup.length <= 1) return null;
    return (
      <div className="form-row" style={{ marginBottom: 'var(--spacing-md)' }}>
        <div className="form-group full-width">
          {/* This modal is always an add/edit context (no view mode), so the
              scope stays editable. onSelectAccount is omitted when the pair is
              locked because the customer itself can't be changed there. */}
          <ApplyScopeControl
            members={pricingGroup}
            accountId={form.account_id}
            applyScope={applyScope}
            scopeAccountIds={scopeAccountIds}
            onScopeChange={setApplyScope}
            onAccountsChange={setScopeAccountIds}
            onSelectAccount={locked ? undefined : (id) => handleChange('account_id', String(id))}
            readOnly={isView}
          />
        </div>
      </div>
    );
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content weekly-cost-modal modal-md ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4 className="modal-title">{isView ? 'View Contract Price' : (editingId != null ? 'Edit Contract Price' : 'Add Contract Price')}</h4>
          <button className="btn-icon close-btn" onClick={handleClose} title="Close">
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: 'var(--spacing-md) var(--spacing-xl) var(--spacing-xl)' }}>
            {error && (<div className="error-message"><span className="material-symbols-rounded">error</span>{error}</div>)}

            {existing.length > 0 && !isView && editingId == null && (() => {
              const replacing = rows.map(replacedBy).filter(Boolean);
              const replaceLabel = replacing.map(r => formatRange(r)).join(', ');
              return (
                <div className="mp-existing-note">
                  <span className="material-symbols-rounded">history</span>
                  <span>
                    This customer already has {existing.length} price{existing.length !== 1 ? 's' : ''} for this product — your entr{rows.length !== 1 ? 'ies' : 'y'} will be added to their history
                    {replacing.length > 0 ? ` (the price for ${replaceLabel} will be updated)` : ''}.
                  </span>
                </div>
              );
            })()}

            {locked && (
              <div className="info-panel">
                <span style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center', fontWeight: 600, fontSize: '12px' }}>
                  {pair.account_no && <span className="product-code">{pair.account_no}</span>}{pair.company_name}
                </span>
                <span style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center', fontWeight: 600, fontSize: '12px' }}>
                  {pair.item_code && <span className="product-code">{pair.item_code}</span>}{pair.item_name}
                </span>
              </div>
            )}

            {!locked && (
              <div className="form-row" style={{ marginBottom: 'var(--spacing-md)' }}>
                <div className="form-group full-width">
                  <label>Customer <span className="required">*</span></label>
                  <SearchableDropdown
                    value={form.account_id}
                    onChange={(v) => handleChange('account_id', v)}
                    options={customers.map(c => {
                      const addr = addressLine1(c.address);
                      return {
                        value: c.id.toString(), badge: c.account_no || ' ', label: c.company_name,
                        renderLabel: (<>{c.company_name}{addr && <span className="address-line"> ({addr})</span>}</>),
                        searchText: `${c.account_no || ''} ${c.company_name} ${addr}`
                      };
                    })}
                    placeholder="Select customer..."
                    searchPlaceholder="Search by # or name..."
                    disabled={isView}
                    // "Add Contract Price" starts on the customer picker. Only
                    // reached when there's no pair (an edit opens with the
                    // customer already fixed, so opening it there would be noise).
                    autoOpen={!isView}
                  />
                </div>
              </div>
            )}

            {renderApplyScope()}

            {!locked && (
              <div
                className="form-row"
                style={{ display: 'grid', gridTemplateColumns: '1fr', marginBottom: 'var(--spacing-md)', gap: 'var(--spacing-md)' }}
              >
                <div className="form-group">
                  <label>Product <span className="required">*</span></label>
                  <SearchableDropdown
                    value={form.inventory_item_id}
                    onChange={(v) => handleChange('inventory_item_id', v)}
                    options={products.map(p => ({ value: p.inventory_item_id.toString(), badge: p.item_code || ' ', label: p.item_name, searchText: `${p.item_code || ''} ${p.item_name}` }))}
                    placeholder="Select product..."
                    searchPlaceholder="Search products..."
                    disabled={isView}
                  />
                </div>
              </div>
            )}

            {/* Latest price on record — reference only, sits directly above the
                entry rows so it's next to the price being typed. */}
            {latest && latestSale != null && !isNaN(latestSale) && (
              <div className="mp-latest-note">
                <span className="material-symbols-rounded">sell</span>
                <span>
                  Latest sale price <strong>{formatCurrency(latestSale)}</strong> / bag
                  <span className="mp-latest-sep"> · </span>{formatRange(latest)}
                  {latestCost != null && (
                    <>
                      <span className="mp-latest-sep"> · </span>cost {formatCurrency(latestCost)}
                      <span className="mp-latest-sep"> · </span>
                      <span className={latestMargin < 0 ? 'mp-latest-neg' : undefined}>
                        margin {latestMargin >= 0 ? '+' : ''}{formatCurrency(latestMargin)}
                      </span>
                    </>
                  )}
                </span>
              </div>
            )}

            {/* One row per price. The add button lives on the last row only
                (and not when editing one price), but every row reserves its
                column so the fields stay aligned as rows are added. Labels
                render on the first row only. */}
            {rows.map((row, idx) => {
              const isLast = idx === rows.length - 1;
              const rowMargin = marginFor(row);
              return (
                <div key={row.key} className="form-row mp-price-row">
                  <div className="form-group">
                    {idx === 0 && <label>From <span className="required">*</span></label>}
                    <DateInput shortYear value={row.valid_from} onChange={(e) => setRow(row.key, 'valid_from', e.target.value)} disabled={isView} />
                  </div>
                  <div className="form-group">
                    {idx === 0 && <label>To <span className="required">*</span></label>}
                    <DateInput shortYear value={row.valid_to} min={row.valid_from || undefined} onChange={(e) => setRow(row.key, 'valid_to', e.target.value)} disabled={isView} />
                  </div>
                  <div className="form-group">
                    {idx === 0 && <label>Sale Price (£/ bag) <span className="required">*</span></label>}
                    <Input id={`mp-sale-${row.key}`} type="number" value={row.sale_price} onChange={(e) => setRow(row.key, 'sale_price', e.target.value)} placeholder="0.00" required disabled={isView} />
                  </div>
                  <div className="form-group">
                    {idx === 0 && <label>Margin/ bag</label>}
                    <Input type="text" value={rowMargin != null ? `${rowMargin >= 0 ? '+' : ''}${formatCurrency(rowMargin)}` : '—'} disabled />
                  </div>
                  <div className="form-group">
                    {idx === 0 && <label>&nbsp;</label>}
                    {isLast && !isView && editingId == null ? (
                      <button type="button" className="btn-round mp-add-row" onClick={addRow} title="Add another price (from the day after this one)">
                        <div className="icon-wrapper">
                          <span className="material-symbols-rounded">add</span>
                        </div>
                      </button>
                    ) : (
                      <span className="mp-add-spacer" aria-hidden="true" />
                    )}
                  </div>
                </div>
              );
            })}

            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <Input type="text" value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Optional notes" disabled={isView} />
              </div>
            </div>

            {/* How the product posts to the ledger — read-only, from the item. */}
            <ItemAccountsPanel itemId={form.inventory_item_id} />
          </div>

          <div className="modal-footer" style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            {isView ? (
              /* Edit edits the latest price by its id, so it waits for the
                 history (and the row it fills) — before then it would add. */
              <button type="button" className="btn-slideout btn-slideout-primary" onClick={() => setMode('edit')}
                disabled={!historyLoaded} title={historyLoaded ? 'Edit this price' : 'Loading the prices…'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">edit</span></div>
                <span className="btn-label">Edit</span>
              </button>
            ) : (
              <>
                <button type="submit" className="btn-slideout btn-slideout-primary" disabled={loading}>
                  <div className="icon-wrapper"><span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>{loading ? 'sync' : 'save'}</span></div>
                  <span className="btn-label">{loading ? 'Saving...' : 'Save Price'}</span>
                </button>
                <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose} disabled={loading}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                  <span className="btn-label">Cancel</span>
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default MilkPowderContractPriceModal;
